import { useEffect } from 'react';

import { useNamedLogger } from '@eluve/logger';
import { bytesToMB, gbToMB } from '@eluve/utils';

const UsedJSHeapSizeThreshold = 1000;

export const useBrowserMemoryReporting = (
  onMemoryThresholdExceeded: (isExceeded: boolean) => void,
  reportingInterval: number,
) => {
  const logger = useNamedLogger('App');
  useEffect(
    function reportMemoryUsage() {
      const interval = setInterval(() => {
        if (window.performance && window.performance.memory) {
          const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } =
            window.performance.memory;

          const jsHeapSizeLimitMB = bytesToMB(jsHeapSizeLimit);
          const totalJSHeapSizeMB = bytesToMB(totalJSHeapSize);
          const usedJSHeapSizeMB = bytesToMB(usedJSHeapSize);

          // Calculate the total amount of memory left before reaching the upper limit
          const heapLimitDelta = jsHeapSizeLimitMB - usedJSHeapSizeMB;
          const heapLimitPercentage =
            (usedJSHeapSizeMB / jsHeapSizeLimitMB) * 100;

          // Calculate the total amount of memory left before reaching the currently allocated limit
          const usedHeapDelta = totalJSHeapSizeMB - usedJSHeapSizeMB;
          const usedHeapPercentage =
            (usedJSHeapSizeMB / totalJSHeapSizeMB) * 100;

          // Device RAM in GB (e.g., 4, 8, 16)
          const deviceMemoryMB =
            'deviceMemory' in navigator
              ? gbToMB(Number(navigator.deviceMemory))
              : undefined;

          logger.info('Memory usage', {
            jsHeapSizeLimit: jsHeapSizeLimitMB,
            totalJSHeapSize: totalJSHeapSizeMB,
            usedJSHeapSize: usedJSHeapSizeMB,
            heapLimitDelta,
            heapLimitPercentage,
            usedHeapDelta,
            usedHeapPercentage,
            deviceMemory: deviceMemoryMB,
          });

          if (usedJSHeapSizeMB > UsedJSHeapSizeThreshold) {
            logger.info('Prompting user to reload the page to release memory');
            onMemoryThresholdExceeded(true);
          }
        }
      }, reportingInterval);

      return () => {
        clearInterval(interval);
      };
    },
    [logger, onMemoryThresholdExceeded, reportingInterval],
  );
};
